import {
  type StatsigGateName,
  type StatsigLayerName,
  getStatsigClientInstance,
  useGateValue,
  useLayer,
} from '@coursera-web/statsig';

import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import { GOOGLE_PARTNER_ID } from 'bundles/ownable-products-common/constants/partners';
import { usePartner } from 'bundles/payments-common/data/usePartner';

/**
 * Statsig surfaces for the GwG (Grow with Google) S12N post-purchase upsell.
 *
 * This module is the single home for the GwG upsell's Statsig logic:
 *  - the rollout gate (`gwg-s12n-ppu-gate`) and its eligibility hook, and
 *  - future A/B tests on the upsell experience (e.g. copy variants) — add those as Statsig
 *    layers/experiments here (via `useLayer` / `useExperiment` from `@coursera-web/statsig`), so the
 *    gate, eligibility, and experiment logic stay co-located. See `useGwgS12nUpsellCopyVariant` below.
 */
export const GWG_S12N_PPU_GATE: StatsigGateName = 'gwg-s12n-ppu-gate';

/**
 * Whether the GwG S12N post-purchase upsell rollout is enabled for this cart.
 *
 * True when the `gwg-s12n-ppu-gate` Statsig gate is enabled AND the cart's product is tied to the
 * Google partner (partnerId 463). The partnerId is resolved from the cart's product via `usePartner`
 * (it is not available directly on the cart).
 *
 * This hook is the durable, FE-facing entry point for the GwG upsell: components should use it to
 * branch the upsell experience.
 */
export const useIsGwgS12nPpuUpsellEnabled = (cart?: CartsV2): boolean => {
  const isGateEnabled = useGateValue(GWG_S12N_PPU_GATE);
  const { s12n, course, degreeProgram } = usePartner(cart);
  const partnerId = s12n?.partners?.[0]?.id ?? course?.partners?.[0]?.id ?? degreeProgram?.partnerIds?.[0];

  return isGateEnabled && partnerId === GOOGLE_PARTNER_ID;
};

/**
 * Statsig layer + parameter backing the GwG S12N post-purchase upsell **copy** A/B test
 * (MONETIZE-2791). Only the copy is gated behind this experiment — the upsell itself is a pre/post
 * GA change gated by `gwg-s12n-ppu-gate` (see `useIsGwgS12nPpuUpsellEnabled`).
 *
 * These names MUST match the Statsig console config (MONETIZE-2792):
 *  - layer:     `gwg-s12n-ppu-copy-layer`
 *  - parameter: `copy-variant`
 */
export const GWG_S12N_PPU_COPY_LAYER: StatsigLayerName = 'gwg-s12n-ppu-copy-layer';
export const GWG_S12N_PPU_COPY_PARAM = 'copy-variant' as const;
export type GwgS12nUpsellCopyVariant = 'control' | 'variant-1';

export const DEFAULT_GWG_S12N_UPSELL_COPY_VARIANT: GwgS12nUpsellCopyVariant = 'control';

const toCopyVariant = (value: string): GwgS12nUpsellCopyVariant =>
  value === 'variant-1' ? 'variant-1' : DEFAULT_GWG_S12N_UPSELL_COPY_VARIANT;

// Reads the copy variant where the post-purchase upsell renders. This also logs a layer exposure, but
// the impression point is the pay-now click (see `useLogGwgS12nUpsellCopyExposure`), which fires first;
// Statsig dedupes per user, so this resolves to that same first exposure.
export const useGwgS12nUpsellCopyVariant = (cart?: CartsV2): GwgS12nUpsellCopyVariant => {
  const isUpsellEnabled = useIsGwgS12nPpuUpsellEnabled(cart);
  const layer = useLayer(GWG_S12N_PPU_COPY_LAYER);

  if (!isUpsellEnabled) {
    return DEFAULT_GWG_S12N_UPSELL_COPY_VARIANT;
  }

  return toCopyVariant(layer.get(GWG_S12N_PPU_COPY_PARAM, DEFAULT_GWG_S12N_UPSELL_COPY_VARIANT));
};

// Returns a callback that logs the copy experiment impression for an in-scope GwG S12N cart.
export const useLogGwgS12nUpsellCopyExposure = (cart?: CartsV2): (() => void) => {
  const isUpsellEnabled = useIsGwgS12nPpuUpsellEnabled(cart);

  return () => {
    if (!isUpsellEnabled) {
      return;
    }

    const statsigClient = getStatsigClientInstance();
    statsigClient.getLayer(GWG_S12N_PPU_COPY_LAYER).get(GWG_S12N_PPU_COPY_PARAM, DEFAULT_GWG_S12N_UPSELL_COPY_VARIANT);
  };
};
